/**
 * Плитки KPI из ответов `/analytics/dashboard/kpi` (верхняя полоса Dashboard.jsx над
 * разделами) и `/analytics/employees/kpi` (вкладка «Сотрудники»).
 *
 * Строку для показа готовит сервер (`formatted`: «25», «01:29», «0%», «7.50»), здесь она только
 * приводится к русской записи чисел. Пустой `formatted` — «значения нет»: за период нет ни одного
 * звонка с длительностью или ни одной оценки чек-листа. `current` в этом случае — 0, но лишь как
 * база для сравнения с прошлым периодом, а не значение: раньше плитки выводили его как «0» и
 * «0.0», и отсутствие данных выглядело худшим возможным значением.
 *
 * Прошлый период устроен так же: `previousFormatted` пуст — данных за прошлый период нет.
 * `changePercent` сервер отдаёт null, когда сравнивать не с чем: значения нет в одном из
 * периодов или прошлое значение — ноль, а текущее нет (роста с нуля не бывает). Раньше на этих
 * местах стояли «+100%» и «−100%», посчитанные от заглушки.
 */

/** Плитка без значения. */
export const EMPTY_KPI_VALUE = '—';

/** Есть ли у метрики значение за текущий период. */
export const hasKpiValue = (metric) =>
  typeof metric?.formatted === 'string' && metric.formatted.trim() !== '';

const INTEGER_VALUE = /^-?\d+$/;
const DECIMAL_VALUE = /^-?\d+\.\d+%?$/;

/** Разряды и запятая. Время («01:29») остаётся как есть. */
const localizeKpiValue = (value) => {
  const text = value.trim();
  if (INTEGER_VALUE.test(text)) {
    return Number(text).toLocaleString('ru-RU');
  }

  return DECIMAL_VALUE.test(text) ? text.replace('.', ',') : text;
};

export const formatKpiMetric = (metric) =>
  (hasKpiValue(metric) ? localizeKpiValue(metric.formatted) : EMPTY_KPI_VALUE);

/** Изменение, округлённое до показываемой точности: «0,0%» — это не рост и не падение. */
const roundedKpiChange = (metric) =>
  (typeof metric?.changePercent === 'number' ? Math.round(metric.changePercent * 10) / 10 : 0);

/** Подпись сравнения с прошлым периодом («+12,5%», «−3,0%»); null — сравнивать нечего. */
export const formatKpiChange = (metric) => {
  if (!hasKpiValue(metric) || typeof metric.changePercent !== 'number') {
    return null;
  }

  const change = roundedKpiChange(metric);
  const sign = change > 0 ? '+' : change < 0 ? '−' : '';
  return `${sign}${Math.abs(change).toFixed(1).replace('.', ',')}%`;
};

/** Цвет и стрелка сравнения: рост — «positive», падение — «negative», без изменения — «neutral». */
export const resolveKpiChangeType = (metric) => {
  const change = roundedKpiChange(metric);
  if (change === 0) {
    return 'neutral';
  }

  return change > 0 ? 'positive' : 'negative';
};

const hasPreviousKpiValue = (metric) =>
  typeof metric?.previousFormatted === 'string' && metric.previousFormatted.trim() !== '';

/**
 * Подпись на месте сравнения, когда его нет: пустым бывает текущий период, прошлый, а
 * прошлое значение бывает нулём — тогда процента нет, но сам ноль показать можно.
 * Оба случая про прошлый период записаны одинаково: в одной полосе они стоят рядом.
 */
export const describeMissingKpiChange = (metric) => {
  if (!hasKpiValue(metric)) {
    return 'Нет данных за период';
  }

  return `Прошлый период: ${hasPreviousKpiValue(metric) ? localizeKpiValue(metric.previousFormatted) : 'нет данных'}`;
};

/** Сравнение словами для подсказки: на плитке рядом с числом стоит только процент. */
export const describeKpiComparison = (metric) => {
  const change = formatKpiChange(metric);
  return change ? `${change} к прошлому периоду` : describeMissingKpiChange(metric);
};
